import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Play, Smartphone, Monitor } from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";

function MetricCard({ label, value, unit, status }) {
  const statusColors = {
    good: "text-green-400 bg-green-900/20",
    needs_improvement: "text-amber-400 bg-amber-900/20",
    poor: "text-red-400 bg-red-900/20",
  };
  const cls = statusColors[status] || "text-gray-400 bg-gray-800";

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-mono font-bold">{value ?? "—"}</span>
        <span className="text-xs text-gray-500">{unit}</span>
      </div>
      {status && (
        <span
          className={`inline-block text-xs px-2 py-0.5 rounded mt-2 font-medium ${cls}`}
        >
          {status.replace("_", " ")}
        </span>
      )}
    </div>
  );
}

export default function WebVitals({ selectedSite }) {
  const [vitals, setVitals] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [device, setDevice] = useState("mobile");

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${selectedSite.id}/vitals/`, {
        params: { device },
      }),
      api.get(`/websites/${selectedSite.id}/vitals/history/`),
    ])
      .then(([vitalsRes, historyRes]) => {
        setVitals(vitalsRes.data.results || vitalsRes.data);
        setHistory(historyRes.data.results || historyRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite, device]);

  const runCheck = () => {
    if (!selectedSite) return;
    setChecking(true);
    api
      .post(`/websites/${selectedSite.id}/vitals/check/`)
      .then(() => {})
      .catch(() => {})
      .finally(() => setChecking(false));
  };

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view Core Web Vitals
      </div>
    );
  }

  // Get averages from current vitals
  const avg = (field) => {
    if (!vitals.length) return null;
    const sum = vitals.reduce((acc, v) => acc + (v[field] || 0), 0);
    return (sum / vitals.length).toFixed(2);
  };
  const avgStatus = (field) => {
    if (!vitals.length) return null;
    const statuses = vitals.map((v) => v[field]);
    if (statuses.includes("poor")) return "poor";
    if (statuses.includes("needs_improvement")) return "needs_improvement";
    return "good";
  };
  const avgScore = () => {
    if (!vitals.length) return 0;
    const sum = vitals.reduce((acc, v) => acc + (v.performance_score || 0), 0);
    return Math.round(sum / vitals.length);
  };

  const chartData = history.map((h) => ({
    date: new Date(h.recorded_at).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    score: h.performance_score,
    lcp: h.lcp,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Core Web Vitals</h2>
        <div className="flex items-center gap-3">
          {/* Device toggle */}
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
            <button
              onClick={() => setDevice("mobile")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors ${
                device === "mobile"
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400"
              }`}
            >
              <Smartphone size={14} /> Mobile
            </button>
            <button
              onClick={() => setDevice("desktop")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors ${
                device === "desktop"
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400"
              }`}
            >
              <Monitor size={14} /> Desktop
            </button>
          </div>
          <button
            onClick={runCheck}
            disabled={checking}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <Play size={14} />
            {checking ? "Checking..." : "Run Check"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-dark-300 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Metric summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 flex items-center justify-center">
              <ScoreRing score={avgScore()} label="Score" />
            </div>
            <MetricCard
              label="LCP"
              value={avg("lcp")}
              unit="s"
              status={avgStatus("lcp_status")}
            />
            <MetricCard
              label="INP"
              value={avg("inp")}
              unit="ms"
              status={avgStatus("inp_status")}
            />
            <MetricCard
              label="CLS"
              value={avg("cls")}
              unit=""
              status={avgStatus("cls_status")}
            />
            <MetricCard
              label="TTFB"
              value={avg("ttfb")}
              unit="s"
            />
          </div>

          {/* History chart */}
          {chartData.length > 1 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">
                Performance Score Over Time
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#111827",
                        border: "1px solid #1e293b",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#b87333"
                      strokeWidth={2}
                      dot={{ fill: "#b87333", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Per-page table */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-dark-50">
              <h3 className="text-sm font-semibold text-gray-300">
                Per-Page Scores
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-dark-50">
                    <th className="text-left py-2.5 px-4 font-medium">URL</th>
                    <th className="text-right py-2.5 px-3 font-medium">
                      Score
                    </th>
                    <th className="text-right py-2.5 px-3 font-medium">LCP</th>
                    <th className="text-right py-2.5 px-3 font-medium">INP</th>
                    <th className="text-right py-2.5 px-3 font-medium">CLS</th>
                  </tr>
                </thead>
                <tbody>
                  {vitals.map((v, i) => (
                    <tr
                      key={i}
                      className="border-b border-dark-50/50 hover:bg-dark-50/30"
                    >
                      <td className="py-2.5 px-4 font-mono text-xs text-gray-300 truncate max-w-xs">
                        {v.url}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">
                        {v.performance_score}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {v.lcp?.toFixed(2)}s
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {v.inp?.toFixed(0)}ms
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {v.cls?.toFixed(3)}
                      </td>
                    </tr>
                  ))}
                  {!vitals.length && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-12 text-center text-gray-500"
                      >
                        No vitals data yet. Click "Run Check" to start.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
